import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SingleFieldFormModalData } from 'src/app/interface/components/forms/single-field-form.interface';

@Component({
  selector: 'common-single-field-form-modal',
  templateUrl: './single-field-form-modal.component.html',
  styleUrls: ['./single-field-form-modal.component.scss'],
})
export class SingleFieldFormModalComponent implements OnInit {
  @Input() title: string = '';
  @Input() inputLabel: string = '';
  @Input() inputPlaceholder: string = '';
  @Input() inputHint: string = '';
  @Input() prefixText: string = '';
  @Input() suffixText: string = '';
  @Input() currentValue: string = '';
  @Input() labelButtonCancel: string = 'maia.doors.button.cancel';
  @Input() labelButtonSave: string = 'maia.doors.button.save';
  @Input() loading: boolean = false;

  @Output() save = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup;
  inputControl: FormControl;
  checkboxControl?: FormControl;
  checkboxOption?: SingleFieldFormModalData['checkboxOption'];
  private lastEnabledValue: string = '';

  constructor(
    private fb: FormBuilder,
    private matDialogRef: MatDialogRef<SingleFieldFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SingleFieldFormModalData,
  ) {
    if (this.data) {
      this.title = this.data.title || this.title;
      this.inputLabel = this.data.inputLabel || this.inputLabel;
      this.inputPlaceholder = this.data.inputPlaceholder || this.inputPlaceholder;
      this.inputHint = this.data.inputHint || this.inputHint;
      this.prefixText = this.data.prefixText || this.prefixText;
      this.suffixText = this.data.suffixText || this.suffixText;
      this.currentValue = this.data.currentValue || this.currentValue;
      this.labelButtonCancel = this.data.labelButtonCancel || this.labelButtonCancel;
      this.labelButtonSave = this.data.labelButtonSave || this.labelButtonSave;
      this.checkboxOption = this.data.checkboxOption;
    }

    const optionalInteger = !!this.data?.numericIntegerOnly && !!this.data?.numericIntegerOptional;
    const strictInteger = !!this.data?.numericIntegerOnly && !optionalInteger;
    const decimalNonNegative = !!this.data?.numericDecimalNonNegative;

    const validators: ValidatorFn[] = [];
    if (!optionalInteger) {
      validators.push(Validators.required);
    }

    if (strictInteger) {
      validators.push(Validators.pattern(/^\d+$/));
      const minInclusive = this.data?.numericMinInclusive;
      const maxInclusive = this.data?.numericMaxInclusive;
      if (minInclusive != null || maxInclusive != null) {
        validators.push(this._integerRangeValidator(minInclusive ?? 0, maxInclusive));
      }
    } else if (optionalInteger) {
      validators.push(
        this._optionalIntegerRangeValidator(
          this.data?.numericMinInclusive ?? 1,
          this.data?.numericMaxInclusive,
        ),
      );
    } else if (decimalNonNegative) {
      validators.push(
        this._nonNegativeDecimalRangeValidator(
          this.data?.numericMinInclusive ?? 0,
          this.data?.numericMaxInclusive,
        ),
      );
    }

    this.inputControl = new FormControl(this.currentValue, validators);
    const group: Record<string, FormControl> = { input: this.inputControl };

    if (strictInteger || optionalInteger) {
      this.inputControl.valueChanges.pipe(distinctUntilChanged()).subscribe((v) => {
        const sanitized = String(v ?? '').replace(/\D/g, '');
        if (sanitized !== v) {
          this.inputControl.setValue(sanitized, { emitEvent: false });
        }
      });
    } else if (decimalNonNegative) {
      this.inputControl.valueChanges.pipe(distinctUntilChanged()).subscribe((v) => {
        const sanitized = this._sanitizeNonNegativeDecimalInput(v);
        if (sanitized !== v) {
          this.inputControl.setValue(sanitized, { emitEvent: false });
        }
      });
    }

    if (this.checkboxOption) {
      const initialChecked = !!this.checkboxOption.checkedByDefault;
      this.checkboxControl = new FormControl(initialChecked);
      group['checkbox'] = this.checkboxControl;

      this.lastEnabledValue = this.currentValue ?? '';

      if (initialChecked) {
        this.applyCheckboxState(true);
      }

      this.checkboxControl.valueChanges.subscribe((checked: unknown) => {
        this.applyCheckboxState(Boolean(checked));
      });
    }

    this.form = this.fb.group(group);
  }

  /** `inputmode` nativo para teclados móviles cuando el campo es numérico. */
  get inputModeAttr(): string | null {
    if (this.data?.numericIntegerOnly) {
      return 'numeric';
    }
    if (this.data?.numericDecimalNonNegative) {
      return 'decimal';
    }
    return null;
  }

  ngOnInit(): void {
    const initial = this.currentValue != null ? String(this.currentValue) : '';
    this.inputControl.setValue(initial, { emitEvent: false });
  }

  private applyCheckboxState(checked: boolean): void {
    const forced = this.checkboxOption?.forcedValue ?? '';
    if (checked) {
      this.lastEnabledValue = String(this.inputControl.value ?? '');
      this.inputControl.setValue(forced, { emitEvent: false });
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
      this.inputControl.setValue(this.lastEnabledValue, { emitEvent: false });
    }
  }

  onEventClose(): void {
    this.matDialogRef.close();
  }

  onEventCancel(): void {
    this.cancel.emit();
    this.matDialogRef.close();
  }

  onEventSave(): void {
    if (this.form.valid) {
      let value: unknown = this.inputControl.value;
      if (this.data?.numericDecimalNonNegative && typeof value === 'string') {
        value = value.trim().replace(',', '.');
      }
      this.save.emit(value as string);
      this.matDialogRef.close(value as string);
    }
  }

  private _integerRangeValidator(minInclusive: number, maxInclusive: number | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const s = String(control.value ?? '').trim();
      if (s === '') {
        return null;
      }
      if (!/^\d+$/.test(s)) {
        return null;
      }
      const n = parseInt(s, 10);
      if (Number.isNaN(n)) {
        return null;
      }
      if (n < minInclusive) {
        return { numericMin: { min: minInclusive } };
      }
      if (maxInclusive != null && n > maxInclusive) {
        return { numericMax: { max: maxInclusive } };
      }
      return null;
    };
  }

  private _optionalIntegerRangeValidator(minInclusive: number, maxInclusive: number | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const s = String(control.value ?? '').trim();
      if (s === '') {
        return null;
      }
      if (!/^\d+$/.test(s)) {
        return { pattern: true };
      }
      const n = parseInt(s, 10);
      if (Number.isNaN(n)) {
        return { pattern: true };
      }
      if (n < minInclusive) {
        return { numericMin: { min: minInclusive } };
      }
      if (maxInclusive != null && n > maxInclusive) {
        return { numericMax: { max: maxInclusive } };
      }
      return null;
    };
  }

  private _nonNegativeDecimalRangeValidator(
    minInclusive: number,
    maxInclusive: number | undefined,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const s = String(control.value ?? '').trim();
      if (s === '') {
        return { required: true };
      }
      const n = this._parseNonNegativeDecimal(s);
      if (n == null) {
        return { pattern: true };
      }
      if (n < minInclusive) {
        return { numericMin: { min: minInclusive } };
      }
      if (maxInclusive != null && n > maxInclusive) {
        return { numericMax: { max: maxInclusive } };
      }
      return null;
    };
  }

  private _parseNonNegativeDecimal(raw: string): number | null {
    const t = raw.trim();
    if (!t) {
      return null;
    }
    if (!/^\d+([.,]\d*)?$/.test(t)) {
      return null;
    }
    const normalized = t.includes(',') && !t.includes('.') ? t.replace(',', '.') : t.replace(',', '');
    const n = Number.parseFloat(normalized);
    if (!Number.isFinite(n)) {
      return null;
    }
    if (n < 0) {
      return null;
    }
    return n;
  }

  /**
   * Mantiene un único separador decimal (`,` o `.`) y solo dígitos válidos como prefijo.
   */
  private _sanitizeNonNegativeDecimalInput(raw: unknown): string {
    let s = String(raw ?? '').replace(/[^\d.,]/g, '');
    if (s === '') {
      return '';
    }
    let sepIndex = -1;
    let sepChar = '';
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === '.' || ch === ',') {
        sepIndex = i;
        sepChar = ch;
        break;
      }
    }
    if (sepIndex === -1) {
      return s;
    }
    const intPart = s.slice(0, sepIndex).replace(/\D/g, '');
    const fracRaw = s.slice(sepIndex + 1).replace(/\D/g, '');
    return `${intPart}${sepChar}${fracRaw}`;
  }
}
